import RatePlanCalculationTypeEnum from '@constants/enums/ratePlan/calculationType';

const toAmount = value => {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    const amount = typeof value === 'object' ? value.amount : value;
    if (amount === null || amount === undefined || amount === '') {
        return null;
    }

    const parsed = Number(amount);
    return Number.isFinite(parsed) ? parsed : null;
};

const roundMoney = value => Math.round(Number(value) * 100) / 100;

const money = (amount, currency = 'GBP') => ({
    amount: roundMoney(amount),
    currency
});

/** A total from `value`/`quantity`, or the sum of a `value_breakdown`/`quantity_breakdown`. */
const toTotal = (single, breakdown) =>
    breakdown ? Object.values(breakdown).reduce((sum, item) => sum + (toAmount(item) || 0), 0) : toAmount(single) || 0;

const firstEntry = (entries = []) => (Array.isArray(entries) && entries.length ? entries[0] : null);

/**
 * Preview the annual fee for the rate-plan value checker (mock / UI contract).
 * Applies a simple tier-1 rate, optional surcharge, then the annual min/max clamps.
 * Mirrors the real CheckValueSchema request shape: a nested `plan`, and `value`/`quantity` or their breakdowns.
 * Per-product rates are not modelled: a breakdown is charged as its total at the tier-1 rate.
 */
export const previewRatePlanValue = (payload = {}) => {
    const plan = payload?.plan || {};

    const currency = payload?.value?.currency || plan?.minimum_fee?.currency || plan?.maximum_fee?.currency || 'GBP';

    const calculationType = plan?.calculation_type || RatePlanCalculationTypeEnum.VALUE;
    const entry = firstEntry(plan.entries);
    const minimumFee = toAmount(plan.minimum_fee);
    const maximumFee = toAmount(plan.maximum_fee);
    const surcharge = toAmount(plan.surcharge) || 0;

    let calculated = 0;
    let effectiveRate = null;

    if (calculationType === RatePlanCalculationTypeEnum.QUANTITY) {
        const quantity = toTotal(payload.quantity, payload.quantity_breakdown);
        const ratePerUnit = toAmount(entry?.rate_per_unit) ?? 1;
        calculated = quantity * ratePerUnit;
        // effective_rate stays null, as the backend's does: a per-unit average is money, not a rate.
    } else {
        const aua = toTotal(payload.value, payload.value_breakdown);
        const rate = toAmount(entry?.rate) ?? 0.0025;
        calculated = aua * rate;
        effectiveRate = rate;
    }

    let fee = calculated + surcharge;

    if (minimumFee !== null && fee < minimumFee) {
        fee = minimumFee;
    }

    if (maximumFee !== null && fee > maximumFee) {
        fee = maximumFee;
    }

    return {
        fee: money(fee, currency),
        effective_rate: effectiveRate
    };
};

export default previewRatePlanValue;
